import { useTranslation } from "@redotech/redo-i18n";
import { ConditionTypeDetailsProps } from "@redotech/redo-return-ui/flows/return-flow/condition";
import { MessageBodyMatchesState } from "@redotech/redo-return-ui/flows/rules/conditions/message-body-matches";
import { RedoCheckbox } from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import { RedoTagInputField } from "@redotech/redo-web/arbiter-components/input/redo-tag-input-field";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { LabeledInput } from "@redotech/redo-web/labeled-input";
import { memo, useState } from "react";
import { commitPendingTagInput } from "../commit-pending-tag-input";

export const MessageBodyMatchesDetails = memo(
  function MessageBodyMatchesDetails({
    state,
    setState,
    disabled,
  }: ConditionTypeDetailsProps<MessageBodyMatchesState>) {
    const { t } = useTranslation();
    const [inputValue, setInputValue] = useState("");

    const matchModeOptions = [
      {
        id: "INCLUDES",
        type: "text" as const,
        text: t("contains"),
        value: "INCLUDES",
      },
      {
        id: "IS_EXACTLY",
        type: "text" as const,
        text: t("entire message is exactly"),
        value: "IS_EXACTLY",
      },
    ];

    const selectedMatchMode = matchModeOptions.find(
      (option) => option.id === state.matchMode,
    );

    const commitPendingInput = () => {
      if (disabled) {
        return;
      }
      const queryStrings = commitPendingTagInput(
        state.queryStrings || [],
        inputValue,
      );
      if (!queryStrings) {
        return;
      }
      setState({ ...state, queryStrings });
      setInputValue("");
    };

    return (
      <>
        <RedoSingleSelectDropdownInput
          disabled={disabled}
          label={t("Match mode")}
          options={matchModeOptions}
          optionSelected={({ item }) => {
            setState({
              ...state,
              // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
              matchMode: item.id as typeof state.matchMode,
            });
          }}
          selectedItem={selectedMatchMode}
        />
        <RedoTagInputField
          delimiters={["newline"]}
          inputValue={inputValue}
          label={t("Message body text to match")}
          onBlur={commitPendingInput}
          placeholder={t("Enter message body text to match")}
          setInputValue={setInputValue}
          setTags={(queryStrings) => {
            const uniqueStrings = Array.from(new Set(queryStrings));
            setState({ ...state, queryStrings: uniqueStrings });
          }}
          state={disabled ? "disabled" : "default"}
          tags={(state.queryStrings || []).map((text) => ({ text }))}
        />

        <Flex>
          <LabeledInput label={t("Case sensitive")}>
            <RedoCheckbox
              setValue={(value) => {
                if (typeof value === "boolean") {
                  setState({ ...state, caseSensitive: value });
                }
              }}
              value={state.caseSensitive}
            />
          </LabeledInput>{" "}
        </Flex>
      </>
    );
  },
);
